/* ============================================================
   DS Cart Drawer — JavaScript
   Handles: open/close, AJAX cart updates, quantity changes
   ============================================================ */

(function () {
  'use strict';

  const DRAWER_ID      = 'ds-cart-drawer';
  const OVERLAY_ID     = 'ds-drawer-overlay';
  const CLOSE_BTN_ID   = 'ds-drawer-close';
  const ITEMS_ID       = 'ds-drawer-items';
  const FOOTER_ID      = 'ds-drawer-footer';
  const EMPTY_ID       = 'ds-drawer-empty';
  const SUBTOTAL_ID    = 'ds-drawer-subtotal';

  // ── Helpers ────────────────────────────────────────────────

  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }

  function suppressOldSidebar() {
    // Hide the theme's native cart sidebar so it never conflicts with our drawer
    const oldSidebar = document.getElementById('site-cart-sidebar');
    if (oldSidebar && typeof oldSidebar.hide === 'function') {
      oldSidebar.hide();
    } else if (oldSidebar) {
      oldSidebar.classList.remove('sidebar--opened');
      oldSidebar.style.display = 'none';
    }
    document.body.classList.remove('sidebar-opened', 'sidebar-opened--right');
  }

  function openDrawer() {
    suppressOldSidebar();
    const drawer = document.getElementById(DRAWER_ID);
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    // Focus close button for a11y
    const closeBtn = document.getElementById(CLOSE_BTN_ID);
    if (closeBtn) setTimeout(() => closeBtn.focus(), 380);
  }

  function closeDrawer() {
    const drawer = document.getElementById(DRAWER_ID);
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  function setLoading(on) {
    const panel = qs('.ds-drawer__panel');
    if (!panel) return;
    panel.classList.toggle('ds-loading', on);
  }


  // ── Cart AJAX fetch ─────────────────────────────────────────

  async function fetchCart() {
    const resp = await fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } });
    return resp.json();
  }

  async function updateItem(key, qty) {
    setLoading(true);
    try {
      const resp = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty })
      });
      const cart = await resp.json();
      requestAnimationFrame(function() { renderDrawer(cart).then(function() { updateCartCount(cart.item_count); }); });
    } catch (e) {
      console.error('[DS Drawer] updateItem error:', e);
    } finally {
      setLoading(false);
    }
  }

  // ── Render drawer from cart JSON ────────────────────────────

  async function renderDrawer(cart) {
    const itemsEl   = document.getElementById(ITEMS_ID);
    const footerEl  = document.getElementById(FOOTER_ID);
    const emptyEl   = document.getElementById(EMPTY_ID);
    const subtotalEl = document.getElementById(SUBTOTAL_ID);

    if (!itemsEl) return;

    const isEmpty = !cart.items || cart.items.length === 0;

    // Toggle empty/filled states
    emptyEl  && emptyEl.classList.toggle('ds-hidden', !isEmpty);
    itemsEl  && itemsEl.classList.toggle('ds-hidden',  isEmpty);
    footerEl && footerEl.classList.toggle('ds-hidden',  isEmpty);

    if (subtotalEl) {
      subtotalEl.textContent = formatMoney(cart.total_price, window.dsMoneyFormat || '\u20AC{{amount_with_comma_separator}}');
    }

    if (isEmpty) return;

    // Build items HTML
    let html = '';
    for (const item of cart.items) {
      const imgSrc = item.image
        ? item.image.replace(/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i, (_m, ext, qs) => '_240x300.' + ext + (qs || ''))
        : '';
      const imgHtml = imgSrc
        ? '<img src="' + imgSrc + '" alt="' + escHtml(item.title) + '" width="90" height="112" loading="lazy">'
        : '';
      const variantHtml = (item.variant_title && item.variant_title !== 'Default Title')
        ? '<p class="ds-drawer__item-variant">' + escHtml(item.variant_title) + '</p>'
        : '';
      const priceFmt = item.final_line_price;

      html += `<div class="ds-drawer__item" data-key="${escAttr(item.key)}">
  <a href="${escAttr(item.url)}" class="ds-drawer__item-image-link">
    <div class="ds-drawer__item-image">${imgHtml}</div>
  </a>
  <div class="ds-drawer__item-details">
    <div class="ds-drawer__item-top">
      <div>
        <p class="ds-drawer__item-artist">${escHtml(item.vendor)}</p>
        <a href="${escAttr(item.url)}" class="ds-drawer__item-name">${escHtml(item.product_title)}</a>
        ${variantHtml}
      </div>
      <p class="ds-drawer__item-price">${formatMoneyFromItem(priceFmt)}</p>
    </div>
    <div class="ds-drawer__item-bottom">
      <div class="ds-drawer__qty" data-item-key="${escAttr(item.key)}">
        <button class="ds-drawer__qty-btn ds-qty-minus" aria-label="Decrease quantity"
          data-key="${escAttr(item.key)}" data-qty="${item.quantity - 1}"
          ${item.quantity <= 1 ? 'disabled' : ''}>−</button>
        <span class="ds-drawer__qty-num">${item.quantity}</span>
        <button class="ds-drawer__qty-btn ds-qty-plus" aria-label="Increase quantity"
          data-key="${escAttr(item.key)}" data-qty="${item.quantity + 1}">+</button>
      </div>
      <button class="ds-drawer__remove" aria-label="Remove ${escAttr(item.product_title)}"
        data-key="${escAttr(item.key)}" data-qty="0">REMOVE</button>
    </div>
  </div>
</div>`;
    }

    itemsEl.innerHTML = html;
    bindItemEvents(itemsEl);
  }

  // ── Money formatting ────────────────────────────────────────

  function formatMoneyFromItem(cents) {
    return formatMoney(cents, window.dsMoneyFormat || '\u20AC{{amount_with_comma_separator}}');
  }

  function formatMoney(cents, format) {
    var fmt = format || window.dsMoneyFormat || '\u20AC{{amount_with_comma_separator}}';
    var n = (cents / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return fmt.replace('{{amount_with_comma_separator}}', n).replace('{{amount}}', (cents / 100).toFixed(2));
  }

  // ── Security helpers ────────────────────────────────────────
  function escHtml(str) {
    return String(str).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
  }
  function escAttr(str) { return escHtml(str); }

  // ── Event binding on items ──────────────────────────────────

  function bindItemEvents(ctx) {
    ctx.querySelectorAll('[data-qty]').forEach(btn => {
      btn.addEventListener('click', function () {
        const key = this.dataset.key;
        const qty = parseInt(this.dataset.qty, 10);
        updateItem(key, Math.max(0, qty));
      });
    });
  }

  // ── Cart count update ───────────────────────────────────────

  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count], .site-header__cart-count, .cart-count, .js-cart-count, [data-cart-item-count]').forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
    // Pulse animation on cart icon
    document.querySelectorAll('.site-header__cart, .cart-icon-wrapper, [data-cart-icon]').forEach(el => {
      el.classList.remove('ds-cart-icon-pulse');
      void el.offsetWidth; // reflow
      el.classList.add('ds-cart-icon-pulse');
    });
  }

  // ── Intercept Add to Cart ───────────────────────────────────

  async function handleAddToCart(form) {
    if (!form) return;
    const btn = form.querySelector('[type="submit"], .product__add-to-cart, [name="add"]');
    const originalText = btn ? btn.textContent : '';

    // Disable button
    if (btn) { btn.disabled = true; btn.textContent = '...'; }

    try {
      const formData = new FormData(form);
      const resp = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: formData
      });

      if (!resp.ok) throw new Error('Add to cart failed');

      const cart = await fetchCart();
      openDrawer();
      requestAnimationFrame(function() { renderDrawer(cart).then(function() { updateCartCount(cart.item_count); }); });

    } catch (e) {
      console.error('[DS Drawer] add to cart error:', e);
      // Fall back to default form submit
      if (btn) { btn.disabled = false; btn.textContent = originalText; }
      form.submit();
      return;
    }

    if (btn) { btn.disabled = false; btn.textContent = originalText; }
  }

  // ── Init ────────────────────────────────────────────────────

  function init() {
    // Close on overlay click
    const overlay = document.getElementById(OVERLAY_ID);
    if (overlay) overlay.addEventListener('click', closeDrawer);

    // Close on close button
    const closeBtn = document.getElementById(CLOSE_BTN_ID);
    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);

    // Close on Escape key
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') closeDrawer();
    });

    // Listen for the theme's native 'add-to-cart' event fired by product-form element
    // This fires AFTER the theme has already added the item to cart
    document.addEventListener('add-to-cart', async function(e) {
      try {
        const cart = await fetchCart();
        openDrawer();
        requestAnimationFrame(function() { renderDrawer(cart).then(function() { updateCartCount(cart.item_count); }); });
      } catch(err) {
        console.error('[DS Drawer] add-to-cart handler error:', err);
      }
    }, true);

    // Fallback: also intercept direct form submits for any product form not using the custom element
    document.addEventListener('submit', function(e) {
      const form = e.target;
      if (form.tagName !== 'FORM') return;
      if (!form.action || !form.action.includes('/cart/add')) return;
      // Only intercept if NOT inside a product-form custom element (which handles it natively)
      if (form.closest('product-form[data-ajax-cart]')) return;
      e.preventDefault();
      handleAddToCart(form);
    }, true);

    // Expose openDrawer globally (for theme JS hooks)
    window.dsOpenCartDrawer  = openDrawer;
    window.dsCloseCartDrawer = closeDrawer;
    window.dsRenderDrawer    = renderDrawer;

    // Expose money format
    window.dsMoneyFormat = document.querySelector('meta[name="money-format"]')?.content
      || '{{amount_with_comma_separator}} €';

    // Intercept the theme's native cart sidebar — patch its show() method
    // so it can never open on top of our drawer
    function patchOldSidebar() {
      const oldSidebar = document.getElementById('site-cart-sidebar');
      if (!oldSidebar) return;
      if (oldSidebar._dsPatchApplied) return;
      oldSidebar._dsPatchApplied = true;
      const origShow = oldSidebar.show?.bind(oldSidebar);
      if (origShow) {
        oldSidebar.show = function() {
          // Do nothing — our drawer handles cart display
        };
      }
      // If already open on page load, close it immediately
      if (oldSidebar.classList.contains('sidebar--opened')) {
        suppressOldSidebar();
      }
    }

    // Patch immediately and after short delay (in case sidebar-drawer registers late)
    patchOldSidebar();
    setTimeout(patchOldSidebar, 300);
    setTimeout(patchOldSidebar, 1000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();